import React from 'react';
import { X, AlertTriangle, Lock } from 'lucide-react';

const EliminarClaseModal = ({ isOpen, onClose, clase, validacion, onEliminar, loading }) => {
  if (!isOpen) return null;

  const puedeEliminar = validacion && validacion.puede_eliminar;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className={`p-6 rounded-t-lg flex justify-between items-center ${puedeEliminar ? 'bg-red-600' : 'bg-gray-600'}`}>
          <div className="flex items-center gap-3 text-white">
            {puedeEliminar ? (
              <AlertTriangle size={32} />
            ) : (
              <Lock size={32} />
            )}
            <div>
              <h2 className="text-xl font-bold">
                {puedeEliminar ? 'Eliminar Clase' : 'No se puede eliminar'}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {puedeEliminar ? (
            /* PUEDE ELIMINAR - Clase vacía */
            <>
              <p className="text-gray-700 mb-4">
                ¿Estás seguro que deseas eliminar la clase <strong>"{clase?.nombre || clase?.titulo}"</strong>?
              </p>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                <div className="flex gap-3">
                  <i className="fas fa-check-circle text-green-600 mt-1"></i>
                  <div className="text-sm text-green-800">
                    <p className="font-semibold mb-2">Esta clase está vacía y puede eliminarse:</p>
                    <ul className="space-y-1 text-xs">
                      <li>✓ Sin estudiantes inscritos</li>
                      <li>✓ Sin calificaciones registradas</li>
                      <li>✓ Sin asistencias</li>
                      <li>✓ Sin respuestas de formularios</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <div className="flex gap-3">
                  <i className="fas fa-exclamation-triangle text-red-600 mt-1"></i>
                  <div className="text-sm text-red-800">
                    <p className="font-semibold">⚠️ Esta acción no se puede deshacer</p>
                    <p className="text-xs mt-1">Los materiales, formularios y tareas de esta clase también serán eliminados.</p>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  onClick={onEliminar}
                  className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash"></i>
                      Sí, Eliminar
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            /* NO PUEDE ELIMINAR - Clase con datos */
            <>
              <p className="text-gray-700 mb-4">
                Esta clase contiene información académica registrada y <strong>no puede ser eliminada</strong>.
              </p>

              {validacion && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <div className="flex gap-3">
                    <i className="fas fa-exclamation-circle text-red-600 mt-1"></i>
                    <div className="text-sm text-red-800">
                      <p className="font-semibold mb-2">Información académica registrada:</p>
                      <ul className="space-y-1">
                        {validacion.data?.estudiantes > 0 && (
                          <li>• <strong>{validacion.data.estudiantes}</strong> estudiantes inscritos</li>
                        )}
                        {validacion.data?.calificaciones > 0 && (
                          <li>• <strong>{validacion.data.calificaciones}</strong> calificaciones</li>
                        )}
                        {validacion.data?.asistencias > 0 && (
                          <li>• <strong>{validacion.data.asistencias}</strong> asistencias registradas</li>
                        )}
                        {validacion.data?.respuestas > 0 && (
                          <li>• <strong>{validacion.data.respuestas}</strong> respuestas de formularios</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex gap-3">
                  <i className="fas fa-info-circle text-blue-600 mt-1"></i>
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Para preservar el historial académico:</p>
                    <p className="text-xs">Esta clase debe <strong>archivarse</strong> en lugar de eliminarse. Así mantienes el historial disponible para consultas futuras.</p>
                  </div>
                </div>
              </div>

              {/* Botón */}
              <button
                onClick={onClose}
                className="w-full px-4 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cerrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EliminarClaseModal;
